import { diffInCalendarDays } from '@/lib/dates';

import type { Contract, ContractActionName } from '@/types/api/contract';

/*
 * Règles de lecture des contrats.
 *
 * Reprises telles quelles des anciens sélecteurs redux (`contracts.selectors`,
 * `contract.selector`), devenues des fonctions pures sur la donnée de l'API.
 */

const ACL_CODES = ['7311_ACL', '229127_AC', '229127_CC', '229127_PR', '229127_FR'];

const isNVEIorVAEProduct = (code?: string) => {
    if (!code) return false;
    const upper = code.toUpperCase();
    return upper.includes('NVEI') || upper.includes('VAE') || upper.includes('TROT') || upper.includes('VELO');
};

/* ---------- Liste de contrats ---------- */

export const getFirstContractInProgress = (contracts: Contract[] = []) =>
    contracts.find((c) => c.situation?.statutCode === '1');

export const getPrelevementContracts = (contracts: Contract[] = []) => contracts.find((c) => c.prelevement);

export const hasContractTrottinette = (contracts: Contract[] = []) =>
    contracts.some((c) => c.vehicule?.codeProduit === '7311_NVEI');

export const hasContractVAE = (contracts: Contract[] = []) =>
    contracts.some((c) => c.vehicule?.codeProduit === '7311_VAE');

export const hasContractVAEUnder14Days = (contracts: Contract[] = []) =>
    contracts.some(
        (c) =>
            c.vehicule?.codeProduit === '7311_VAE' &&
            !!c.situation?.dateAffaireNouvelle &&
            diffInCalendarDays(new Date(), c.situation.dateAffaireNouvelle) <= 14,
    );

/** Au moins un contrat qui n'est pas une trottinette. */
export const hasContractOtherThanTrottinette = (contracts: Contract[] = []) =>
    contracts.some((c) => c.vehicule?.codeProduit !== '7311_NVEI');

/** Au moins un contrat hors mobilité douce (NVEI, VAE, trottinette, vélo). */
export const hasOtherContract = (contracts: Contract[] = []) =>
    contracts.some((c) => !isNVEIorVAEProduct(c.vehicule?.codeProduit));

export const getContractsWithCV = (contracts: Contract[] = []) =>
    contracts.filter((c) => c.dernierCourrierCV !== null);

export const hasContractApporteur43397 = (contracts: Contract[] = []) =>
    contracts.some((c) => String(c.apporteur1) === '43397');

export const hasOnlyContractApporteur43397 = (contracts: Contract[] = []) =>
    contracts.length > 0 && contracts.every((c) => String(c.apporteur1) === '43397');

/** Apporteur 1 du dernier contrat de la liste (coordonnées du conseiller), ou `null`. */
export const getApporteur1LastContract = (contracts: Contract[] = []) =>
    contracts[contracts.length - 1]?.apporteurs?.apporteur1 || null;

export const getApporteurNomLastContract = (contracts: Contract[] = []) =>
    contracts[contracts.length - 1]?.apporteurs?.apporteur1?.raisonSociale || null;

/**
 * Restreint l'espace assuré aux fonctionnalités flotte pour les utilisateurs COOLTRA
 * (`user.name` contenant « COOLTRA », insensible à la casse).
 *
 * On se fonde sur le nom de l'utilisateur et non sur ses contrats : une route qui ne charge pas
 * les contrats (ex : `/mes-avantages` en accès direct) laisserait sinon passer la vue complète
 * le temps du chargement.
 */
export const isFleetUserName = (name?: string) => typeof name === 'string' && name.toUpperCase().includes('COOLTRA');

/* ---------- Contrat ---------- */

export const isACLContract = (contract?: Contract) => ACL_CODES.includes(contract?.vehicule?.codeProduit ?? '');
export const isWakamContract = (contract?: Contract) => contract?.compagnie === 'WAKAM';
export const isMAEContract = (contract?: Contract) => contract?.vehicule?.codeProduit === 'MAE_NVEI';
export const isVAEContract = (contract?: Contract) => contract?.vehicule?.codeProduit === '7311_VAE';
export const isNVEIContract = (contract?: Contract) => contract?.vehicule?.codeProduit === '7311_NVEI';
export const isAIMCOContract = (contract?: Contract) =>
    contract?.vehicule?.codeProduit === '7311_QMCO' || contract?.vehicule?.codeProduit === '7311_2MCO';

/** Lignes techniques de réduction de taxe Allianz (« Réduction Allianz Taxe 9 % »…) : pas des garanties, à masquer. */
export const isReductionAllianz = (garantie: { libelle?: string }) =>
    /^\s*r[ée]duction allianz/i.test(garantie.libelle ?? '');

/** Garanties avec la franchise vide corrigée en « 0€ ». */
export const getContractGarantiesOptions = (contract?: Contract) =>
    contract?.garanties?.map((garantie) => ({
        ...garantie,
        franchise: garantie.franchise.replace(/Franchise de (\s*)€/i, 'Franchise de 0€'),
    }));

export const getGarantiesContract = (contract?: Contract) => (contract?.garanties ?? []).filter((g) => g.type === 'G');
export const getOptionsContract = (contract?: Contract) => (contract?.garanties ?? []).filter((g) => g.type === 'O');

export const getContractFractionnementLabel = (contract?: Contract) =>
    contract?.situation?.fractionnement?.endsWith('mensuel') ? 'mensuel' : 'annuel';

export const getDernierHivernageContract = (contract?: Contract) => contract?.hivernages?.[0] ?? null;

/* ---------- Actions ---------- */

/** L'action est proposée à l'assuré et réalisable. */
export const isActionAvailable = (contract: Contract | undefined, action: ContractActionName) =>
    contract?.actions?.[action]?.status === 'available';

/** Motif, rédigé pour l'assuré, d'une action proposée mais bloquée. */
export const getActionBlockedReason = (contract: Contract | undefined, action: ContractActionName) => {
    const state = contract?.actions?.[action];
    return state?.status === 'blocked' ? state.reason?.message : undefined;
};
